import type { BreadcrumbTrailItem } from '../ui/breadcrumb';
import {
  sectionConfigs,
  type SectionConfig,
  type SectionConfigItem,
  type SectionNavGroup,
  type SectionNavItem,
} from '../navigation/sectionConfigs';

interface RouteLocationLike {
  pathname: string;
  search?: string;
}

const STATIC_ROUTE_LABELS: Record<string, string> = {
  '/home': 'Home',
  '/home/widgets': 'Widget Dashboard',
  '/workspaces': 'Workspaces',
  '/documents/library': 'Knowledge Base',
  '/data-connections': 'Data Connections',
  '/ttc-data-products': 'TTC Data Products',
  '/catalog': 'Catalog',
  '/telemetry': 'Telemetry',
  '/evals': 'RAG Evaluations',
  '/gepa-optimizer': 'GEPA Optimizer',
  '/prompt-management': 'Prompt Management',
  '/prd-generator': 'PRD Refiner',
  '/content-research': 'Content Research',
  '/sow': 'SOW Automation',
  '/microsite-generator': 'Microsite Generator',
  '/client-opportunity-ranker': 'Opportunity Ranker',
  '/opportunity-diagnostic-intelligence': 'Opportunity Diagnostic Intelligence',
  '/pulse': 'PULSE',
  '/executive-command-center': 'Executive Command Center',
  '/adoption': 'Adoption Analytics',
  '/agent-configuration': 'Agent Configuration',
  '/platform-admin/admins': 'Admin Management',
  '/platform-admin/providers': 'AI Providers',
  '/platform-admin/email': 'Platform Email',
  '/platform-admin/jobs': 'Job Scheduler',
  '/platform-admin/sso-policy': 'SSO Policy',
  '/platform-admin/tenants': 'Tenant Management',
  '/platform-admin/features': 'Feature Allocation',
  '/platform-admin/adoption': 'Adoption Access',
  '/tenant-admin/users': 'Users & Roles',
  '/tenant-admin/theme': 'Theme',
  '/tenant-admin/email': 'Email Settings',
  '/tenant-admin/sso': 'SSO Settings',
  '/admin/settings': 'AI Providers',
  '/admin/adoption-settings': 'Adoption Settings',
  '/admin/jobs': 'Job Scheduler',
  '/admin/mcp-servers': 'Outbound Access (MCP)',
  '/admin/catalog/corrections': 'Catalog Corrections',
  '/admin/ontology': 'Semantic Ontology',
  '/admin/context-packs': 'Context Packs',
};

const STATIC_ROUTE_PARENTS: Record<string, BreadcrumbTrailItem> = {
  '/platform-admin/admins': { label: 'Platform Settings', path: '/platform-admin/admins' },
  '/platform-admin/providers': { label: 'Platform Settings', path: '/platform-admin/admins' },
  '/platform-admin/email': { label: 'Platform Settings', path: '/platform-admin/admins' },
  '/platform-admin/jobs': { label: 'Platform Settings', path: '/platform-admin/admins' },
  '/platform-admin/sso-policy': { label: 'Platform Settings', path: '/platform-admin/admins' },
  '/platform-admin/tenants': { label: 'Platform Settings', path: '/platform-admin/admins' },
  '/platform-admin/features': { label: 'Platform Settings', path: '/platform-admin/admins' },
  '/platform-admin/adoption': { label: 'Platform Settings', path: '/platform-admin/admins' },
  '/admin/settings': { label: 'Admin Settings', path: '/data-connections' },
  '/admin/adoption-settings': { label: 'Admin Settings', path: '/data-connections' },
  '/admin/jobs': { label: 'Admin Settings', path: '/data-connections' },
  '/admin/mcp-servers': { label: 'Admin Settings', path: '/data-connections' },
  '/tenant-admin/users': { label: 'Admin Settings', path: '/data-connections' },
  '/tenant-admin/theme': { label: 'Admin Settings', path: '/data-connections' },
  '/tenant-admin/email': { label: 'Admin Settings', path: '/data-connections' },
  '/tenant-admin/sso': { label: 'Admin Settings', path: '/data-connections' },
};

function normalizePath(path: string): string {
  if (path.length > 1 && path.endsWith('/')) return path.slice(0, -1);
  return path || '/';
}

function pathWithoutQuery(path: string): string {
  return path.split('?')[0];
}

function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function templateToRegex(pathTemplate: string, exact: boolean): RegExp {
  const templatePath = pathWithoutQuery(pathTemplate);
  const pattern = templatePath
    .split('/')
    .map((part) => (part.startsWith(':') ? '[^/]+' : escapeRegex(part)))
    .join('/');
  return new RegExp(exact ? `^${pattern}$` : `^${pattern}(?:/.*)?$`);
}

function pathMatches(pathTemplate: string, currentPath: string, exact = false): boolean {
  const templatePath = normalizePath(pathWithoutQuery(pathTemplate));
  const normalizedCurrentPath = normalizePath(currentPath);

  if (templatePath.includes(':')) {
    return templateToRegex(templatePath, exact).test(normalizedCurrentPath);
  }

  if (exact) return normalizedCurrentPath === templatePath;
  return normalizedCurrentPath === templatePath || normalizedCurrentPath.startsWith(`${templatePath}/`);
}

function queryMatches(pathTemplate: string, currentSearch: string): boolean {
  const templateSearch = pathTemplate.split('?')[1];
  if (!templateSearch) return true;

  const templateParams = new URLSearchParams(templateSearch);
  const currentParams = new URLSearchParams(currentSearch);

  let matches = true;
  templateParams.forEach((value, key) => {
    if (!matches) return;
    const currentValue = currentParams.get(key);
    if (!currentValue) {
      matches = false;
      return;
    }
    if (!value.startsWith(':') && currentValue !== value) {
      matches = false;
    }
  });

  return matches;
}

function resolvePath(pathTemplate: string, currentPath: string, search = ''): string {
  const [templatePath, templateSearch] = pathTemplate.split('?');
  const currentSegments = normalizePath(currentPath).split('/').filter(Boolean);
  const resolvedSegments = normalizePath(templatePath).split('/').filter(Boolean).map((segment, index) => (
    segment.startsWith(':') ? currentSegments[index] || segment : segment
  ));
  const resolvedPath = `/${resolvedSegments.join('/')}`;
  return templateSearch ? `${resolvedPath}?${templateSearch}` : resolvedPath || search || '/';
}

function firstEnabledItemPath(config: SectionConfig): string | undefined {
  for (const item of config.items) {
    if ('isGroup' in item) {
      const firstGroupItem = item.items.find(subItem => isNavigableItem(subItem));
      if (firstGroupItem) return firstGroupItem.path;
      continue;
    }
    if (isNavigableItem(item)) return item.path;
  }
  return undefined;
}

function isNavigableItem(item: SectionNavItem): boolean {
  return Boolean(item.path)
    && !item.path.startsWith('#')
    && !item.sectionHeader
    && !item.disabled
    && !item.comingSoon;
}

function getMatchingItems(items: SectionNavItem[], pathname: string, search: string): SectionNavItem[] {
  return items
    .filter(item => (
      isNavigableItem(item)
      && queryMatches(item.path, search)
      && pathMatches(item.path, pathname, item.exact)
    ))
    .sort((a, b) => pathWithoutQuery(b.path).length - pathWithoutQuery(a.path).length);
}

function buildRegularSectionBreadcrumbs(
  config: SectionConfig,
  item: SectionNavItem,
  currentPath: string,
  search: string,
): BreadcrumbTrailItem[] {
  const sectionPath = firstEnabledItemPath(config);
  return [
    {
      label: config.title,
      path: sectionPath ? resolvePath(sectionPath, currentPath, search) : undefined,
    },
    {
      label: item.label,
      isCurrent: true,
    },
  ];
}

function buildGroupedSectionBreadcrumbs(
  config: SectionConfig,
  group: SectionNavGroup,
  item: SectionNavItem,
  currentPath: string,
  search: string,
): BreadcrumbTrailItem[] {
  return [
    {
      label: config.title,
      path: resolvePath(group.defaultPath, currentPath, search),
    },
    {
      label: group.label,
      path: resolvePath(group.defaultPath, currentPath, search),
    },
    {
      label: item.label,
      isCurrent: true,
    },
  ];
}

function getWorkspaceScopedExternalBreadcrumbs(
  pathname: string,
  search: string,
): BreadcrumbTrailItem[] | null {
  const params = new URLSearchParams(search);
  const workspaceId = params.get('workspaceId');
  if (!workspaceId) return null;
  const workspaceName = params.get('workspaceName') || `Workspace ${workspaceId}`;

  const labelByPath: Record<string, string> = {
    '/data-connections': 'Data Connections',
    '/telemetry': 'Telemetry',
  };
  const label = labelByPath[normalizePath(pathname)];
  if (!label) return null;

  return [
    { label: 'Workspaces', path: '/workspaces' },
    { label: workspaceName, path: `/workspaces/${workspaceId}` },
    { label, isCurrent: true },
  ];
}

function getCatalogBreadcrumbs(pathname: string): BreadcrumbTrailItem[] | null {
  const normalizedPath = normalizePath(pathname);

  if (normalizedPath === '/catalog') {
    return [{ label: 'Catalog', isCurrent: true }];
  }

  if (normalizedPath.startsWith('/catalog/assets/')) {
    return [
      { label: 'Catalog', path: '/catalog' },
      { label: 'Assets', isCurrent: true },
    ];
  }

  return null;
}

function getSectionBreadcrumbs(pathname: string, search: string): BreadcrumbTrailItem[] | null {
  for (const config of Object.values(sectionConfigs)) {
    for (const entry of config.items as SectionConfigItem[]) {
      if ('isGroup' in entry) {
        const subItem = getMatchingItems(entry.items, pathname, search)[0];
        if (subItem) {
          return buildGroupedSectionBreadcrumbs(config, entry, subItem, pathname, search);
        }
        continue;
      }

      if (!isNavigableItem(entry)) continue;
      if (queryMatches(entry.path, search) && pathMatches(entry.path, pathname, entry.exact)) {
        return buildRegularSectionBreadcrumbs(config, entry, pathname, search);
      }
    }
  }

  return null;
}

function titleizeSegment(segment: string): string {
  return segment
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase());
}

function getStaticBreadcrumbs(pathname: string): BreadcrumbTrailItem[] {
  const normalizedPath = normalizePath(pathname);
  const currentLabel = STATIC_ROUTE_LABELS[normalizedPath]
    ?? titleizeSegment(normalizedPath.split('/').filter(Boolean).slice(-1)[0] || 'Home');
  const parent = STATIC_ROUTE_PARENTS[normalizedPath];

  if (parent && parent.label !== currentLabel) {
    return [
      parent,
      { label: currentLabel, isCurrent: true },
    ];
  }

  return [{ label: currentLabel, isCurrent: true }];
}

export function getRouteBreadcrumbs(location: RouteLocationLike): BreadcrumbTrailItem[] {
  const pathname = normalizePath(location.pathname);
  const search = location.search ?? '';

  return getWorkspaceScopedExternalBreadcrumbs(pathname, search)
    ?? getCatalogBreadcrumbs(pathname)
    ?? getSectionBreadcrumbs(pathname, search)
    ?? getStaticBreadcrumbs(pathname);
}
